import { Num, T } from 'gt-react';
import type { DraftPurchaseOrderFreight as DraftFreight } from 'src/api/rpc/hooks/procurement/useFreightOptions';

// Two decimals, as the order weight is stored, so a 50.4 lb order and a 50.3 lb
// limit never both read "50 lb".
const POUNDS = { maximumFractionDigits: 2 };

/**
 * A draft's weight from its lines' item weights, and a warning when it breaks
 * the chosen freight's weight limits (a courier capped at 50 lb with a
 * 1,000 lb order).
 */
export function DraftFreightLoad({ freight }: { freight: DraftFreight }) {
  if (freight.totalLines === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      <p className="text-muted-foreground">
        <OrderWeight freight={freight} />
      </p>
      <LoadWarning check={freight.loadCheck} />
    </div>
  );
}

function OrderWeight({ freight }: { freight: DraftFreight }) {
  if (freight.weighedLines === 0)
    return (
      <T context="draft PO freight: no line has an item weight">
        Order weight unknown: no line has an item weight.
      </T>
    );
  if (freight.weighedLines < freight.totalLines)
    return (
      <T context="draft PO freight: weight of the lines whose item weight is known">
        Order weight: at least{' '}
        <Num options={POUNDS}>{freight.orderWeightLbs}</Num> lb (
        <Num>{freight.weighedLines}</Num> of <Num>{freight.totalLines}</Num>{' '}
        lines have an item weight)
      </T>
    );
  return (
    <T context="draft PO freight: total order weight in pounds">
      Order weight: <Num options={POUNDS}>{freight.orderWeightLbs}</Num> lb
    </T>
  );
}

function LoadWarning({ check }: { check: DraftFreight['loadCheck'] }) {
  if (check.status === 'over_limit')
    return (
      <p className="text-amber-700">
        <T context="draft PO freight warning: order heavier than the carrier's limit">
          This order is over the <Num options={POUNDS}>{check.limitLbs}</Num> lb
          limit of the chosen freight.
        </T>
      </p>
    );
  if (check.status === 'under_limit')
    return (
      <p className="text-amber-700">
        <T context="draft PO freight warning: order lighter than the freight's minimum">
          This order is under the <Num options={POUNDS}>{check.limitLbs}</Num>{' '}
          lb minimum of the chosen freight.
        </T>
      </p>
    );
  if (check.status === 'unknown_weight')
    return (
      <p className="text-amber-700">
        <T context="draft PO freight warning: weight limit cannot be checked">
          The chosen freight has a weight limit, but this order's weight is not
          fully known.
        </T>
      </p>
    );
  return null;
}
